import { useCallback } from 'react';
import { useGlobalStore } from '../stores/globalStore';
import {
  toFacilityDateString,
  todayFacilityDateString,
  toFacilityIsoDateTime,
} from '../utils/localDate';

/**
 * Date helpers already bound to the current facility's time zone.
 *
 * Call sites almost never want the browser's calendar day — they want the
 * kitchen's. A regional manager in New York opening a California facility at
 * 11pm ET should still see that facility's *current* service day, not
 * tomorrow's.
 *
 * `tblFacility.TimeZone_ID` holds a Windows ID ("Eastern Standard Time");
 * `windowsToIana` inside the util handles the conversion and falls back to
 * browser-local when the facility has none set.
 *
 *   const { today, formatDate } = useFacilityDate();
 *   settingsJson: JSON.stringify({ DateFor: today(), … })
 */
export function useFacilityDate() {
  const facilityWinTz = useGlobalStore((s) => s.facilitySettings?.timeZone_ID ?? null);

  const today = useCallback(
    () => todayFacilityDateString(facilityWinTz),
    [facilityWinTz],
  );

  const formatDate = useCallback(
    (d: Date) => toFacilityDateString(d, facilityWinTz),
    [facilityWinTz],
  );

  const formatIsoDateTime = useCallback(
    (d: Date = new Date()) => toFacilityIsoDateTime(d, facilityWinTz),
    [facilityWinTz],
  );

  return { today, formatDate, formatIsoDateTime, facilityWinTz };
}
